import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  CalendarClock,
  BriefcaseBusiness,
  BellRing,
  Building2,
  Check,
  CheckCircle2,
  CircleAlert,
  Clock3,
  Filter,
  FileUp,
  History,
  MessageSquare,
  Paperclip,
  Plus,
  Pencil,
  Search,
  StickyNote,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import { useNavigate, useSearchParams } from "../../app/router";
import { readToken, tenantTokenKey } from "../../app/session";
import { SearchableSelect } from "../../components/SearchableSelect";
import { CroatianDateInput } from "../../components/CroatianDateInput";
import { MobileVirtualList, useMobileViewport } from "../../components/MobileVirtualList";
import { SwipeReveal } from "../../components/SwipeReveal";
import { useTranslation } from "../../i18n/i18n";
import { ApiError, apiDownload, apiRequest } from "../../lib/api";
import { formatDateTimeValue } from "../../lib/croatian-date";
import { useTenantApp } from "../app/TenantAppContext";
import { NotesPanel } from "./NotesPanel";

interface Task {
  id: string;
  number: number;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  dueAt: string | null;
  creatorName: string;
  creatorId: string;
  canDelete: boolean;
  assigneeIds: string[];
  assigneeNames: string[];
  checklist: { total: number; done: number };
  partnerId: string | null;
  partnerName: string | null;
  workOrderIds: string[];
  workOrderNumbers: number[];
}

interface TaskUser {
  id: string;
  firstName: string;
  lastName: string;
  jobTitle: string | null;
  roleKey: string | null;
  mentorIds: string[];
}

interface PartnerOption {
  id: string;
  code: string;
  name: string;
  oib: string | null;
  city: string;
}

interface TaskDetail {
  id: string;
  number: number;
  partnerId: string;
  partnerName: string | null;
  title: string;
  description: string;
  status: string;
  priority: string;
  dueAt: string | null;
  createdAt: string;
  updatedAt: string;
  creatorName: string;
  assigneeIds: string[];
  checklist: Array<{
    id: string;
    title: string;
    completed: boolean;
    completedAt: string | null;
    completedByName: string | null;
  }>;
  comments: Array<{ id: string; body: string; authorName: string; createdAt: string }>;
  history: Array<{
    id: string;
    fromStatus: string | null;
    toStatus: string;
    changedAt: string;
    changedByName: string;
  }>;
  attachments: Array<{
    id: string;
    filename: string;
    mimeType: string;
    sizeBytes: number;
    kind: 'ATTACHMENT' | 'PHOTO';
    createdAt: string;
    uploadedByName: string;
    fileUrl: string;
  }>;
  workOrders: Array<{ id: string; number: number; title: string; status: string }>;
}

function readFileDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("Datoteku nije moguće pročitati."));
    reader.readAsDataURL(file);
  });
}

const emptyForm = {
  partnerId: "",
  title: "",
  description: "",
  priority: "NORMAL",
  dueAt: "",
  assigneeIds: [] as string[],
  checklistText: "",
};

const workflowStatuses = [
  "NEW",
  "ACKNOWLEDGED",
  "IN_PROGRESS",
  "BLOCKED",
  "DONE",
];

const taskFilters = [
  { value: "OPEN", label: "Otvoreni" },
  { value: "ALL", label: "Svi zadaci" },
  { value: "DONE", label: "Završeni" },
];

function statusLabel(status: string): string {
  return (
    {
      NEW: "Novo",
      ACKNOWLEDGED: "Preuzeto",
      IN_PROGRESS: "U tijeku",
      BLOCKED: "Blokirano",
      DONE: "Završeno",
      CANCELLED: "Otkazano",
    }[status] ?? status
  );
}

function priorityLabel(priority: string): string {
  return (
    { LOW: "Nizak", NORMAL: "Normalan", HIGH: "Visok", URGENT: "Hitno" }[
      priority
    ] ?? priority
  );
}

export function TasksPage() {
  const navigate = useNavigate();
  const { context } = useTenantApp();
  const { locale, t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const canCreate = context.user.permissions.includes("tasks.create");
  const canUpdate = context.user.permissions.includes("tasks.update");
  const isMobile = useMobileViewport();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<TaskUser[]>([]);
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [teams, setTeams] = useState<Array<{ id: string; name: string }>>([]);
  const [partnerFilter, setPartnerFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [mentorFilter, setMentorFilter] = useState("");
  const [dueFrom, setDueFrom] = useState("");
  const [dueTo, setDueTo] = useState("");
  const [status, setStatus] = useState("OPEN");
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"TASKS" | "NOTES">(
    params.get("view") === "notes" ? "NOTES" : "TASKS"
  );
  const [modal, setModal] = useState(params.get("new") === "1");
  const [editing, setEditing] = useState<Task | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [detail, setDetail] = useState<TaskDetail | null>(null);
  const [comment, setComment] = useState("");
  const taskParam = params.get("task");

  async function load() {
    setError("");
    try {
      const query = new URLSearchParams({ status, search });
      if (partnerFilter) query.set("partnerId", partnerFilter);
      if (teamFilter) query.set("teamId", teamFilter);
      if (assigneeFilter) query.set("assigneeId", assigneeFilter);
      if (mentorFilter) query.set("mentorId", mentorFilter);
      if (dueFrom) query.set("dueFrom", dueFrom);
      if (dueTo) query.set("dueTo", dueTo);
      const [taskResponse, optionsResponse] = await Promise.all([
        apiRequest<{ items: Task[] }>(`/api/v1/tasks?${query}`, {
          accessToken: readToken(tenantTokenKey),
        }),
        apiRequest<{
          users: TaskUser[];
          partners: PartnerOption[];
          teams: Array<{ id: string; name: string }>;
        }>("/api/v1/tasks/options", {
          accessToken: readToken(tenantTokenKey),
        }),
      ]);
      setTasks(taskResponse.items);
      setUsers(optionsResponse.users);
      setPartners(optionsResponse.partners);
      setTeams(optionsResponse.teams);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Zadaci nisu dostupni.",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const delay = window.setTimeout(() => void load(), 180);
    return () => window.clearTimeout(delay);
  }, [search, status, partnerFilter, teamFilter, assigneeFilter, mentorFilter, dueFrom, dueTo]);

  useEffect(() => {
    if (taskParam && detail?.id !== taskParam) void openDetail(taskParam, false);
  }, [taskParam]);

  async function createTask(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await apiRequest<{
        id?: string;
        number?: number;
        notificationsQueued?: number;
      }>(editing ? `/api/v1/tasks/${editing.id}` : "/api/v1/tasks", {
        method: editing ? "PATCH" : "POST",
        accessToken: readToken(tenantTokenKey),
        body: JSON.stringify({
          ...form,
          dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : "",
          checklist: form.checklistText
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean),
        }),
      });
      setModal(false);
      setEditing(null);
      setParams({});
      setForm(emptyForm);
      setAssigneeSearch("");
      setNotice(
        editing
          ? response.notificationsQueued
            ? `Promjene su spremljene. ${response.notificationsQueued} novododijeljenih zaposlenika dobiva e-mail i PWA obavijest.`
            : "Promjene su spremljene."
          : `Zadatak #${response.number ?? ""} je dodijeljen. E-mail i PWA obavijesti su stavljeni u pouzdani red slanja.`,
      );
      await load();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Zadatak nije moguće spremiti.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function setTaskStatus(taskId: string, nextStatus: string) {
    setError("");
    try {
      await apiRequest(`/api/v1/tasks/${taskId}/status`, {
        method: "PATCH",
        accessToken: readToken(tenantTokenKey),
        body: JSON.stringify({ status: nextStatus }),
      });
      await load();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Status zadatka nije moguće promijeniti.",
      );
    }
  }

  const grouped = useMemo(
    () => ({
      active: tasks.filter(
        (task) => !["DONE", "CANCELLED"].includes(task.status),
      ),
      done: tasks.filter((task) => task.status === "DONE"),
    }),
    [tasks],
  );

  const partnerOptions = useMemo(
    () =>
      partners.map((partner) => ({
        value: partner.id,
        label: `${partner.code} · ${partner.name}`,
        searchText: `${partner.name} ${partner.code} ${partner.oib ?? ""} ${partner.city}`,
        description: [partner.oib ? `OIB ${partner.oib}` : "", partner.city]
          .filter(Boolean)
          .join(" · "),
      })),
    [partners],
  );

  const formatDate = (value: string) => formatDateTimeValue(value);

  function openNew() {
    setForm(emptyForm);
    setEditing(null);
    setModal(true);
    setError("");
  }

  async function openEdit(task: Task) {
    const detail = await apiRequest<{
      partnerId: string;
      title: string;
      description: string;
      priority: string;
      dueAt: string | null;
      assigneeIds: string[];
      checklist: Array<{ title: string }>;
    }>(`/api/v1/tasks/${task.id}`, { accessToken: readToken(tenantTokenKey) });
    const date = detail.dueAt ? new Date(detail.dueAt) : null;
    const localDue = date
      ? new Date(date.getTime() - date.getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16)
      : "";
    setEditing(task);
    setForm({
      partnerId: detail.partnerId,
      title: detail.title,
      description: detail.description,
      priority: detail.priority,
      dueAt: localDue,
      assigneeIds: detail.assigneeIds,
      checklistText: detail.checklist.map((item) => item.title).join("\n"),
    });
    setModal(true);
    setError("");
  }

  async function openDetail(taskId: string, updateUrl = true) {
    setError("");
    try {
      const response = await apiRequest<TaskDetail>(`/api/v1/tasks/${taskId}`, {
        accessToken: readToken(tenantTokenKey),
      });
      setDetail(response);
      setComment("");
      if (updateUrl) setParams({ task: taskId });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Detalji zadatka nisu dostupni.");
      if (!updateUrl) setParams({});
    }
  }

  async function toggleChecklist(item: TaskDetail["checklist"][number]) {
    if (!detail) return;
    await apiRequest(`/api/v1/tasks/${detail.id}/checklist/${item.id}`, {
      method: "PATCH",
      accessToken: readToken(tenantTokenKey),
      body: JSON.stringify({ completed: !item.completed }),
    });
    await openDetail(detail.id, false);
    await load();
  }

  async function addComment(event: FormEvent) {
    event.preventDefault();
    if (!detail || !comment.trim()) return;
    setBusy(true);
    try {
      await apiRequest(`/api/v1/tasks/${detail.id}/comments`, {
        method: "POST",
        accessToken: readToken(tenantTokenKey),
        body: JSON.stringify({ body: comment }),
      });
      setComment("");
      await openDetail(detail.id, false);
    } finally {
      setBusy(false);
    }
  }

  async function uploadAttachment(file: File, kind: "ATTACHMENT" | "PHOTO") {
    if (!detail) return;
    setBusy(true);
    setError("");
    try {
      await apiRequest(`/api/v1/tasks/${detail.id}/attachments`, {
        method: "POST",
        accessToken: readToken(tenantTokenKey),
        body: JSON.stringify({
          originalFilename: file.name,
          fileDataUrl: await readFileDataUrl(file),
          kind,
        }),
      });
      await openDetail(detail.id, false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Privitak nije moguće spremiti.");
    } finally {
      setBusy(false);
    }
  }

  async function openAttachment(attachment: TaskDetail["attachments"][number]) {
    const { blob } = await apiDownload(attachment.fileUrl, readToken(tenantTokenKey));
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  async function removeTask(task: Task) {
    if (!window.confirm(`Obrisati zadatak #${task.number} „${task.title}”?`)) return;
    setError("");
    try {
      await apiRequest(`/api/v1/tasks/${task.id}`, {
        method: "DELETE",
        accessToken: readToken(tenantTokenKey),
      });
      setNotice(`Zadatak #${task.number} je obrisan.`);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Zadatak nije moguće obrisati.");
    }
  }

  function showNotes() {
    setView("NOTES");
    setDetail(null);
    setModal(false);
    setParams({ view: "notes" });
  }

  function showTasks() {
    setView("TASKS");
    setParams({});
  }

  if (view === "NOTES") {
    return (
      <div className="module-page">
        <header className="module-page__hero">
          <div>
            <span className="eyebrow">OPERATIVA · KOMUNIKACIJA</span>
            <h1>Bilješke</h1>
            <p>Objavljene informacije vidi cijela firma; radničke objave prolaze brzo odobrenje.</p>
          </div>
        </header>
        <nav className="page-tabs task-page-tabs" aria-label="Zadaci i bilješke">
          <button type="button" onClick={showTasks}><CheckCircle2 /> Zadaci</button>
          <button type="button" className="is-active" onClick={showNotes}><StickyNote /> Bilješke</button>
        </nav>
        <NotesPanel />
      </div>
    );
  }

  return (
    <div className="module-page">
      <header className="module-page__hero">
        <div>
          <span className="eyebrow">OPERATIVA · ZADACI</span>
          <h1>{t("tasks.title")}</h1>
          <p>{t("tasks.subtitle")}</p>
        </div>
        {canCreate && (
          <button className="button button--primary" data-mobile-fab onClick={openNew} aria-label="Novi zadatak">
            <Plus size={18} /> {t("action.newTask")}
          </button>
        )}
      </header>

      <nav className="page-tabs task-page-tabs" aria-label="Zadaci i bilješke">
        <button type="button" className="is-active" onClick={showTasks}><CheckCircle2 /> Zadaci</button>
        <button type="button" onClick={showNotes}><StickyNote /> Bilješke</button>
      </nav>

      {notice && (
        <button className="task-notice" onClick={() => setNotice("")}>
          <BellRing /> <span>{notice}</span>
          <X />
        </button>
      )}

      <section className="task-summary">
        <article>
          <CheckCircle2 />
          <div>
            <strong>{grouped.active.length}</strong>
            <small>Aktivni zadaci</small>
          </div>
        </article>
        <article>
          <CircleAlert />
          <div>
            <strong>
              {tasks.filter((task) => task.status === "BLOCKED").length}
            </strong>
            <small>Blokirani</small>
          </div>
        </article>
        <article>
          <Clock3 />
          <div>
            <strong>
              {
                tasks.filter(
                  (task) =>
                    task.dueAt &&
                    new Date(task.dueAt) < new Date() &&
                    task.status !== "DONE",
                ).length
              }
            </strong>
            <small>U kašnjenju</small>
          </div>
        </article>
      </section>

      <section className="data-card">
        <header className="data-toolbar">
          <label className="compact-search">
            <Search />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`${t("action.search")}…`}
            />
          </label>
          <label className="compact-filter">
            <Filter />
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {taskFilters.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
              <optgroup label="Točan status">
                {workflowStatuses.filter((item) => item !== "DONE").concat("CANCELLED").map((item) => (
                  <option key={item} value={item}>{statusLabel(item)}</option>
                ))}
              </optgroup>
            </select>
          </label>
          <select
            className="compact-select"
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
          >
            <option value="">Svi timovi</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
          <SearchableSelect
            className="searchable-select--compact"
            value={partnerFilter}
            options={partnerOptions}
            onChange={setPartnerFilter}
            placeholder="Svi partneri"
            searchPlaceholder="Naziv, šifra, OIB ili mjesto…"
            allowClear
            ariaLabel="Filtriraj po partneru"
          />
        </header>
        <div className="task-advanced-filters">
          <label className="field">
            <span>Radnik / izvršitelj</span>
            <select value={assigneeFilter} onChange={(event) => setAssigneeFilter(event.target.value)}>
              <option value="">Svi radnici</option>
              {users.filter((user) => user.roleKey === "worker").map((user) => (
                <option key={user.id} value={user.id}>{user.firstName} {user.lastName}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Mentor radnika</span>
            <select value={mentorFilter} onChange={(event) => setMentorFilter(event.target.value)}>
              <option value="">Svi mentori</option>
              {users.filter((user) => user.roleKey === "mentor").map((user) => (
                <option key={user.id} value={user.id}>{user.firstName} {user.lastName}</option>
              ))}
            </select>
          </label>
          <label className="field"><span>Rok od</span><CroatianDateInput value={dueFrom} onValueChange={setDueFrom} ariaLabel="Rok od" /></label>
          <label className="field"><span>Rok do</span><CroatianDateInput value={dueTo} onValueChange={setDueTo} min={dueFrom} ariaLabel="Rok do" /></label>
          {(assigneeFilter || mentorFilter || dueFrom || dueTo) && (
            <button type="button" className="button button--ghost button--small" onClick={() => { setAssigneeFilter(""); setMentorFilter(""); setDueFrom(""); setDueTo(""); }}>
              Očisti filtre
            </button>
          )}
        </div>
        {error && !modal && <div className="form-error">{error}</div>}
        {!isMobile && <div className="operational-list task-desktop-list">
          {tasks.map((task) => (
            <article
              className={`work-card task-card priority-border--${task.priority.toLowerCase()}`}
              key={task.id}
              role="button"
              tabIndex={0}
              onClick={() => void (canUpdate ? openEdit(task) : openDetail(task.id))}
              onKeyDown={(event) => { if (event.key === "Enter") void (canUpdate ? openEdit(task) : openDetail(task.id)); }}
            >
              {task.canDelete && (
                <button className="mobile-record-delete" onClick={(event) => { event.stopPropagation(); void removeTask(task); }} aria-label={`Obriši zadatak ${task.number}`}>
                  <Trash2 />
                </button>
              )}
              <div className="work-card__number">#{task.number}</div>
              <div className="work-card__body">
                <div>
                  <strong>{task.title}</strong>
                  <span
                    className={`status-pill status-pill--${task.status.toLowerCase()}`}
                  >
                    {statusLabel(task.status)}
                  </span>
                  <span
                    className={`priority-label priority-label--${task.priority.toLowerCase()}`}
                  >
                    {priorityLabel(task.priority)}
                  </span>
                </div>
                {task.description && <p>{task.description}</p>}
                {canUpdate && (
                  <div className="task-card__workflow">
                    <span>Status zadatka</span>
                    <select
                      className={`task-status-control task-status-control--${task.status.toLowerCase()}`}
                      value={task.status}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) =>
                        void setTaskStatus(task.id, event.target.value)
                      }
                      aria-label={`Promijeni status zadatka ${task.number}`}
                    >
                      {workflowStatuses
                        .concat("CANCELLED")
                        .map((item) => (
                          <option key={item} value={item}>
                            {statusLabel(item)}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
                <footer>
                  {task.partnerName && (
                    <span>
                      <Building2 /> {task.partnerName}
                    </span>
                  )}
                  <span>
                    <UserRound /> {task.assigneeNames.join(", ")}
                  </span>
                  <span>
                    <CalendarClock />{" "}
                    {task.dueAt
                      ? formatDate(task.dueAt)
                      : t("common.noDeadline")}
                  </span>
                  {task.checklist.total > 0 && (
                    <span>
                      <Check /> {task.checklist.done}/{task.checklist.total}
                    </span>
                  )}
                  {task.workOrderNumbers.map((number) => (
                    <span className="linked-record" key={number}>
                      <BriefcaseBusiness /> RN-{number}
                    </span>
                  ))}
                </footer>
              </div>
              <div className="stack-actions task-card__actions">
                  <button
                    className="button button--small button--ghost"
                    onClick={(event) => { event.stopPropagation(); void openDetail(task.id); }}
                  >
                    Detalji
                  </button>
                {canUpdate && (
                  <>
                  <button
                    className="button button--small button--ghost"
                    onClick={(event) => { event.stopPropagation(); navigate(`/app/work-orders?new=1&taskId=${task.id}`); }}
                  >
                    <BriefcaseBusiness />{" "}
                    {task.workOrderNumbers.length
                      ? "Još naloga"
                      : "Kreiraj nalog"}
                  </button>
                  <button
                    className="icon-button"
                    onClick={(event) => { event.stopPropagation(); void openEdit(task); }}
                  >
                    <Pencil />
                  </button>
                  </>
                )}
              </div>
            </article>
          ))}
          {!loading && !tasks.length && (
            <div className="table-empty">{t("tasks.empty")}</div>
          )}
        </div>}
        <MobileVirtualList
          className="task-mobile-list"
          items={tasks}
          estimateSize={235}
          getKey={(task) => task.id}
          empty={!loading ? <div className="table-empty">{t("tasks.empty")}</div> : null}
          renderItem={(task) => (
            <SwipeReveal
              actionLabel={task.canDelete ? "Obriši" : "Detalji"}
              ActionIcon={task.canDelete ? Trash2 : CheckCircle2}
              destructive={task.canDelete}
              onAction={() => task.canDelete ? void removeTask(task) : void openDetail(task.id)}
            >
              <article
                className={`task-mobile-card mobile-native-card priority-border--${task.priority.toLowerCase()}`}
                role="button"
                tabIndex={0}
                onClick={() => void (canUpdate ? openEdit(task) : openDetail(task.id))}
                onKeyDown={(event) => { if (event.key === "Enter") void (canUpdate ? openEdit(task) : openDetail(task.id)); }}
              >
                {task.canDelete && <button className="mobile-record-delete" onClick={(event) => { event.stopPropagation(); void removeTask(task); }} aria-label={`Obriši zadatak ${task.number}`}><Trash2 /></button>}
                <header><span>#{task.number}</span><div><strong>{task.title}</strong><small>{task.partnerName ?? "Interni zadatak"}</small></div><span className={`status-pill status-pill--${task.status.toLowerCase()}`}>{statusLabel(task.status)}</span></header>
                {task.description && <p>{task.description}</p>}
                <div className="task-mobile-card__people"><UserRound /><span><small>Izvršitelji</small><strong>{task.assigneeNames.join(", ")}</strong></span></div>
                <footer><span><CalendarClock /> {task.dueAt ? formatDate(task.dueAt) : t("common.noDeadline")}</span>{task.checklist.total > 0 && <span><Check /> {task.checklist.done}/{task.checklist.total}</span>}<span className={`priority-label priority-label--${task.priority.toLowerCase()}`}>{priorityLabel(task.priority)}</span></footer>
                {canUpdate && <select className={`task-status-control task-status-control--${task.status.toLowerCase()}`} value={task.status} onClick={(event) => event.stopPropagation()} onChange={(event) => void setTaskStatus(task.id, event.target.value)} aria-label={`Promijeni status zadatka ${task.number}`}>{workflowStatuses.concat("CANCELLED").map((item) => <option key={item} value={item}>{statusLabel(item)}</option>)}</select>}
              </article>
            </SwipeReveal>
          )}
        />
      </section>

      {modal && (
        <div className="modal-backdrop">
          <form
            className="operational-modal operational-modal--task"
            onSubmit={createTask}
          >
            <header>
              <div>
                <span className="eyebrow">
                  {editing ? "UREĐIVANJE ZADATKA" : "NOVI POSAO"}
                </span>
                <h2>
                  {editing
                    ? `Uredi zadatak #${editing.number}`
                    : t("action.newTask")}
                </h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => {
                  setModal(false);
                  setEditing(null);
                  setParams({});
                }}
              >
                <X />
              </button>
            </header>
            <div className="modal-scroll">
              <div className="form-grid">
                <label className="field field--full">
                  <span>Naslov zadatka</span>
                  <input
                    autoFocus
                    required
                    value={form.title}
                    onChange={(e) =>
                      setForm({ ...form, title: e.target.value })
                    }
                    placeholder="Što treba napraviti?"
                  />
                </label>
                <label className="field field--full">
                  <span>Opis</span>
                  <textarea
                    rows={4}
                    value={form.description}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                    placeholder="Kontekst, očekivani rezultat i važne napomene…"
                  />
                </label>
                <div className="field">
                  <span>Partner / naručitelj</span>
                  <SearchableSelect
                    value={form.partnerId}
                    options={partnerOptions}
                    onChange={(partnerId) => setForm({ ...form, partnerId })}
                    placeholder="Interni zadatak"
                    searchPlaceholder="Naziv, šifra, OIB ili mjesto…"
                    allowClear
                    ariaLabel="Odaberi partnera ili naručitelja"
                  />
                </div>
                <label className="field">
                  <span>{t("tasks.priority")}</span>
                  <select
                    value={form.priority}
                    onChange={(e) =>
                      setForm({ ...form, priority: e.target.value })
                    }
                  >
                    <option value="LOW">Nizak</option>
                    <option value="NORMAL">Normalan</option>
                    <option value="HIGH">Visok</option>
                    <option value="URGENT">Hitno</option>
                  </select>
                </label>
                <label className="field">
                  <span>{t("tasks.due")}</span>
                  <CroatianDateInput
                    withTime
                    value={form.dueAt}
                    onValueChange={(value) => setForm({ ...form, dueAt: value })}
                    ariaLabel="Rok zadatka"
                  />
                </label>
                <fieldset className="assignee-picker field--full">
                  <legend>{t("tasks.assignees")}</legend>
                  <label className="assignee-picker__search">
                    <Search />
                    <input
                      value={assigneeSearch}
                      onChange={(event) =>
                        setAssigneeSearch(event.target.value)
                      }
                      placeholder="Traži zaposlenika po imenu ili radnom mjestu…"
                    />
                  </label>
                  <div>
                    {users
                      .filter((user) =>
                        `${user.firstName} ${user.lastName} ${user.jobTitle ?? ""}`
                          .toLocaleLowerCase(locale)
                          .includes(
                            assigneeSearch.trim().toLocaleLowerCase(locale),
                          ),
                      )
                      .map((user) => (
                        <label
                          key={user.id}
                          className={
                            form.assigneeIds.includes(user.id)
                              ? "is-selected"
                              : ""
                          }
                        >
                          <input
                            type="checkbox"
                            checked={form.assigneeIds.includes(user.id)}
                            onChange={() =>
                              setForm({
                                ...form,
                                assigneeIds: form.assigneeIds.includes(user.id)
                                  ? form.assigneeIds.filter(
                                      (id) => id !== user.id,
                                    )
                                  : [...form.assigneeIds, user.id],
                              })
                            }
                          />
                          <span className="avatar avatar--soft">
                            {user.firstName[0]}
                            {user.lastName[0]}
                          </span>
                          <span>
                            <strong>
                              {user.firstName} {user.lastName}
                            </strong>
                            <small>{user.jobTitle ?? "Zaposlenik"}</small>
                          </span>
                        </label>
                      ))}
                  </div>
                </fieldset>
                <label className="field field--full">
                  <span>Checklist (jedna stavka u svakom retku)</span>
                  <textarea
                    rows={4}
                    value={form.checklistText}
                    onChange={(e) =>
                      setForm({ ...form, checklistText: e.target.value })
                    }
                    placeholder={
                      "Pripremiti materijal\nNazvati klijenta\nPoslati potvrdu"
                    }
                  />
                </label>
              </div>
              {error && <div className="form-error">{error}</div>}
            </div>
            <footer>
              {editing && (
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => {
                    const taskId = editing.id;
                    setModal(false);
                    setEditing(null);
                    void openDetail(taskId);
                  }}
                >
                  Detalji, komentari i privitci
                </button>
              )}
              <button
                type="button"
                className="button button--ghost"
                onClick={() => setModal(false)}
              >
                {t("action.cancel")}
              </button>
              <button
                className="button button--primary"
                disabled={busy || !form.assigneeIds.length}
              >
                {busy
                  ? "Spremam…"
                  : editing
                    ? "Spremi promjene"
                    : "Dodijeli zadatak i pošalji e-mail"}
              </button>
            </footer>
          </form>
        </div>
      )}

      {detail && (
        <div className="modal-backdrop">
          <section className="operational-modal task-detail-modal">
            <header>
              <div>
                <span className="eyebrow">ZADATAK #{detail.number}</span>
                <h2>{detail.title}</h2>
                <p>{detail.partnerName ?? "Interni zadatak"} · kreirao {detail.creatorName}</p>
              </div>
              <button className="icon-button" onClick={() => { setDetail(null); setParams({}); }}><X /></button>
            </header>
            <div className="modal-scroll">
              <div className="task-detail__status">
                <span className={`status-pill status-pill--${detail.status.toLowerCase()}`}>{statusLabel(detail.status)}</span>
                <span className={`priority-label priority-label--${detail.priority.toLowerCase()}`}>{priorityLabel(detail.priority)}</span>
                <span><CalendarClock /> {detail.dueAt ? formatDate(detail.dueAt) : "Bez roka"}</span>
                <span><UserRound /> {detail.assigneeIds.length} izvršitelja</span>
              </div>
              {detail.description && <p className="task-detail__description">{detail.description}</p>}

              <div className="task-detail-grid">
                <section className="task-detail-panel">
                  <header><h3><CheckCircle2 /> Checklist</h3><small>{detail.checklist.filter((item) => item.completed).length}/{detail.checklist.length}</small></header>
                  <div className="detail-checklist">
                    {detail.checklist.map((item) => (
                      <button
                        key={item.id}
                        className={item.completed ? "is-completed" : ""}
                        disabled={!canUpdate}
                        onClick={() => void toggleChecklist(item)}
                      >
                        <span>{item.completed ? <Check /> : <span />}</span>
                        <strong>{item.title}</strong>
                        {item.completedByName && <small>{item.completedByName}</small>}
                      </button>
                    ))}
                    {!detail.checklist.length && <p className="muted">Nema stavki.</p>}
                  </div>
                </section>

                <section className="task-detail-panel">
                  <header><h3><Paperclip /> Privitci i fotografije</h3><small>{detail.attachments.length}</small></header>
                  <div className="detail-files">
                    {detail.attachments.map((attachment) => (
                      <button key={attachment.id} onClick={() => void openAttachment(attachment)}>
                        <Paperclip />
                        <span><strong>{attachment.filename}</strong><small>{attachment.uploadedByName} · {(attachment.sizeBytes / 1024).toFixed(0)} KB</small></span>
                      </button>
                    ))}
                    {!detail.attachments.length && <p className="muted">Još nema privitaka.</p>}
                  </div>
                  {canUpdate && <div className="detail-upload-actions">
                    <label className="button button--ghost button--small"><FileUp /> Dodaj privitak<input type="file" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadAttachment(file, "ATTACHMENT"); event.target.value = ""; }} /></label>
                    <label className="button button--ghost button--small"><FileUp /> Dodaj fotografiju<input type="file" accept="image/*" capture="environment" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadAttachment(file, "PHOTO"); event.target.value = ""; }} /></label>
                  </div>}
                </section>

                <section className="task-detail-panel">
                  <header><h3><MessageSquare /> Komentari</h3><small>{detail.comments.length}</small></header>
                  <div className="detail-comments">
                    {detail.comments.map((item) => <article key={item.id}><strong>{item.authorName}</strong><time>{formatDate(item.createdAt)}</time><p>{item.body}</p></article>)}
                    {!detail.comments.length && <p className="muted">Još nema komentara.</p>}
                  </div>
                  {canUpdate && <form className="detail-comment-form" onSubmit={addComment}><textarea rows={2} value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Dodaj napomenu ili komentar…" /><button className="button button--primary button--small" disabled={busy || !comment.trim()}>Objavi</button></form>}
                </section>

                <section className="task-detail-panel">
                  <header><h3><History /> Povijest</h3><small>{detail.history.length}</small></header>
                  <div className="detail-history">
                    {detail.history.map((item) => <article key={item.id}><span /><div><strong>{item.fromStatus ? `${statusLabel(item.fromStatus)} → ` : ""}{statusLabel(item.toStatus)}</strong><small>{item.changedByName} · {formatDate(item.changedAt)}</small></div></article>)}
                  </div>
                  {detail.workOrders.map((order) => <button className="linked-detail" key={order.id} onClick={() => navigate(`/app/work-orders?order=${order.id}`)}><BriefcaseBusiness /> RN-{order.number} · {order.title}</button>)}
                </section>
              </div>
              {error && <div className="form-error">{error}</div>}
            </div>
            <footer>
              <span>Ažurirano {formatDate(detail.updatedAt)}</span>
              <div>
                {canUpdate && <button className="button button--ghost" onClick={() => { const summary = tasks.find((item) => item.id === detail.id); setDetail(null); if (summary) void openEdit(summary); }}>Uredi zadatak</button>}
                {canUpdate && <button className="button button--primary" onClick={() => navigate(`/app/work-orders?new=1&taskId=${detail.id}`)}><BriefcaseBusiness /> Kreiraj radni nalog</button>}
              </div>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}
